import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useCertificateApi } from '../hooks/useCertificateApi';
import { useAuth } from '../context/AuthContext';

import {
  CertificateResponse,
  CreateCertificateRequest,
} from '../services/certificateApi';

import '../styles/CertificateCreatePage.css';

type DynamicFieldRow = {
  label: string;
  value: string;
};
type CertificateStyle =
  | 'surveillance'
  | 'registration';
type CertificateType =
  | 'ISO'
  | 'Compliance';
type StandardOption = {
  value: string;
  label: string;
};

const CERTIFICATE_TYPE_OPTIONS: {
  value: CertificateType;
  label: string;
}[] = [
  { value: 'ISO', label: 'ISO Certificate' },
  {
    value: 'Compliance',
    label: 'ISO Compliance Standard Certificate',
  },
];

const ISO_STANDARD_OPTIONS: StandardOption[] = [
  { value: 'ISO 9001:2015', label: 'ISO 9001:2015 - Quality Management System (QMS)' },
  { value: 'ISO 14001:2026', label: 'ISO 14001:2026 - Environmental Management System (EMS)' },
  { value: 'ISO 20121:2024', label: 'ISO 20121:2024 - Event Sustainability Management System' },
  { value: 'ISO 21001:2025', label: 'ISO 21001:2025 - Educational Organizations Management System (EOMS)' },
  { value: 'ISO 22000:2018', label: 'ISO 22000:2018 - Food Safety Management System (FSMS)' },
  { value: 'ISO 22301:2019', label: 'ISO 22301:2019 - Business Continuity Management System (BCMS)' },
  { value: 'ISO 28000:2022', label: 'ISO 28000:2022 - Security and Resilience - Security Management Systems' },
  { value: 'ISO 37001:2025', label: 'ISO 37001:2025 - Anti-Bribery Management System (ABMS)' },
  { value: 'ISO 37301:2021', label: 'ISO 37301:2021 - Compliance Management System (CMS)' },
  { value: 'ISO 3834-1:2021', label: 'ISO 3834-1:2021 - Quality Requirements for Fusion Welding of Metallic Materials' },
  { value: 'ISO 39001:2012', label: 'ISO 39001:2012 - Road Traffic Safety Management System (RTSMS)' },
  { value: 'ISO 41001:2018', label: 'ISO 41001:2018 - Facility Management System (FMS)' },
  { value: 'ISO 45001:2018', label: 'ISO 45001:2018 - Occupational Health and Safety Management System' },
  { value: 'ISO 50001:2018', label: 'ISO 50001:2018 - Energy Management Systems' },
  { value: 'ISO 55001:2024', label: 'ISO 55001:2024 - Asset Management System (AMS)' },
  { value: 'ISO 56001:2024', label: 'ISO 56001:2024 - Innovation Management System' },
  { value: 'ISO/IEC 20000-1:2018', label: 'ISO/IEC 20000-1:2018 - IT Service Management System (ITSMS)' },
  { value: 'ISO/IEC 27001:2022', label: 'ISO/IEC 27001:2022 - Information Security Management System' },
  { value: 'ISO/IEC 27701:2025', label: 'ISO/IEC 27701:2025 - Privacy Information Management System (PIMS)' },
  { value: 'ISO/IEC 42001:2023', label: 'ISO/IEC 42001:2023 - Artificial Intelligence Management System (AIMS)' },
];

const COMPLIANCE_STANDARD_OPTIONS: StandardOption[] = [
  { value: 'CE Compliance', label: 'CE Compliance' },
  { value: 'GDPR Compliance', label: 'GDPR Compliance - General Data Protection Regulation' },
  { value: 'GMP', label: 'GMP - Good Manufacturing Practices' },
  { value: 'HACCP', label: 'HACCP - Hazard Analysis and Critical Control Points' },
  { value: 'IATF 16949', label: 'IATF 16949 - Automotive Quality Management System' },
  { value: 'ISO 10001:2018', label: 'ISO 10001:2018 - Quality Management - Customer Satisfaction' },
  { value: 'ISO 10002:2018', label: 'ISO 10002:2018 - Quality Management - Customer Satisfaction & Complaints Handling' },
  { value: 'ISO 14046:2014', label: 'ISO 14046:2014 - Environmental Management - Water Footprint' },
  { value: 'ISO 14064-1:2018', label: 'ISO 14064-1:2018 - Greenhouse Gases - Organizational Level' },
  { value: 'ISO 14067:2018', label: 'ISO 14067:2018 - Carbon Footprint of Products' },
  { value: 'ISO 19011:2026', label: 'ISO 19011:2026 - Guidelines for Auditing Management Systems' },
  { value: 'ISO 20400:2017', label: 'ISO 20400:2017 - Sustainable Procurement - Guidance' },
  { value: 'ISO 22716:2007', label: 'ISO 22716:2007 - Cosmetics - Good Manufacturing Practices (GMP)' },
  { value: 'ISO 26000:2010', label: 'ISO 26000:2010 - Guidance on Social Responsibility' },
  { value: 'ISO 31000:2018', label: 'ISO 31000:2018 - Risk Management - Guidelines' },
  { value: 'ISO/IEC 27017:2026', label: 'ISO/IEC 27017:2026 - Security Controls for Cloud Services' },
  { value: 'ISO/IEC 27018:2025', label: 'ISO/IEC 27018:2025 - Protection of Personally Identifiable Information in Public Clouds' },
  { value: 'ISO/IEC 27032:2023', label: 'ISO/IEC 27032:2023 - Cybersecurity - Guidelines for Internet Security' },
  { value: 'ISO/IEC 27400:2022', label: 'ISO/IEC 27400:2022 - Cybersecurity - IoT Security and Privacy Guidelines' },
  { value: 'ISO/IEC 27402:2023', label: 'ISO/IEC 27402:2023 - IoT Security and Privacy - Device Baseline Requirements' },
  { value: 'PCI DSS', label: 'PCI DSS - Payment Card Industry Data Security Standard' },
  { value: 'REACH Compliance', label: 'REACH Compliance - Registration, Evaluation, Authorisation and Restriction of Chemicals' },
  { value: 'RoHS Compliance', label: 'RoHS Compliance - Restriction of Hazardous Substances' },
  { value: 'SA8000', label: 'SA8000 - Social Accountability Standard' },
  { value: 'SOC 1', label: 'SOC 1 - System and Organization Controls - Financial Reporting' },
  { value: 'SOC 2', label: 'SOC 2 - System and Organization Controls - Trust Services Criteria' },
];

const STANDARD_OPTIONS_BY_TYPE: Record<
  CertificateType,
  StandardOption[]
> = {
  ISO: ISO_STANDARD_OPTIONS,
  Compliance: COMPLIANCE_STANDARD_OPTIONS,
};

/*
  Get today's date without UTC/timezone
  conversion.
*/
const getToday = () => {
  return toInputDate(new Date());
};

/*
  Parse yyyy-MM-dd as a LOCAL date.

  This avoids timezone issues such as:

  01-09-2026
  becoming
  31-08-2026
*/
const parseDateInput = (
  value: string
) => {
  if (!value) {
    return new Date('Invalid Date');
  }

  const parts =
    value.split('-').map(Number);

  if (parts.length !== 3) {
    return new Date('Invalid Date');
  }

  const [year, month, day] =
    parts;

  return new Date(
    year,
    month - 1,
    day,
    12,
    0,
    0
  );
};

const isValidDate = (
  value: Date
) => {
  return !Number.isNaN(
    value.getTime()
  );
};

/*
  Convert local Date to yyyy-MM-dd
  without using toISOString().
*/
const toInputDate = (
  value: Date
) => {
  const year =
    value.getFullYear();

  const month =
    String(
      value.getMonth() + 1
    ).padStart(2, '0');

  const day =
    String(
      value.getDate()
    ).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const plusYears = (
  value: Date,
  years: number
) => {
  const result =
    new Date(value);

  result.setFullYear(
    result.getFullYear() + years
  );

  return result;
};

const plusMonths = (
  value: Date,
  months: number
) => {
  const result =
    new Date(value);

  result.setMonth(
    result.getMonth() + months
  );

  return result;
};

const privacySurveillance = (
  expiryDate: Date
) => {
  const result =
    new Date(expiryDate);

  result.setFullYear(
    result.getFullYear() - 2
  );

  return result;
};

const formatOrdinalDate = (
  value: Date
) => {
  const day =
    value.getDate();

  const suffix =
    day === 11 ||
    day === 12 ||
    day === 13
      ? 'th'
      : day % 10 === 1
        ? 'st'
        : day % 10 === 2
          ? 'nd'
          : day % 10 === 3
            ? 'rd'
            : 'th';

  return `${day}${suffix} ${value.toLocaleString(
    'en-GB',
    {
      month: 'long',
    }
  )} ${value.getFullYear()}`;
};

const formatDashDate = (
  value: Date
) => {
  const formatted =
    value.toLocaleString(
      'en-GB',
      {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }
    );

  return formatted
    .replace(/ /g, '-')
    .toUpperCase();
};

/*
  Determine certificate style.
*/
const resolveCertificateStyle = (
  standard: string
): CertificateStyle => {
  const normalized =
    standard.toUpperCase();

  const isUcsStyle =
    normalized.includes('38-1') ||
    normalized.includes('3834') ||
    normalized.includes('GMP');

  const isPrivacyStyle =
    normalized.includes('GDPR') ||
    normalized.includes('27701');

  return isUcsStyle ||
    isPrivacyStyle
    ? 'registration'
    : 'surveillance';
};

/*
  Certificate validity.

  Default = 3 years.
*/
const getCertificateValidityYears = (
  issueDateValue?: string | null,
  expiryDateValue?: string | null
) => {
  if (
    !issueDateValue ||
    !expiryDateValue
  ) {
    return 3;
  }

  const issueDate =
    parseDateInput(issueDateValue);

  const expiryDate =
    parseDateInput(expiryDateValue);

  if (
    !isValidDate(issueDate) ||
    !isValidDate(expiryDate)
  ) {
    return 3;
  }

  return (
    expiryDate.getFullYear() -
    issueDate.getFullYear()
  );
};

/*
  Automatically generate all
  certificate-related dates.
*/
const getAutoDynamicDateFields = (
  standard: string,
  issueDateValue: string,
  expiryDateValue: string
): Pick<
  CreateCertificateRequest,
  | 'dateOfCertification'
  | 'firstSurveillanceAuditDue'
  | 'secondSurveillanceAuditDue'
  | 'registrationDate'
  | 'surveillanceDate'
> => {
  const issueDate =
    parseDateInput(issueDateValue);

  const expiryDate =
    parseDateInput(expiryDateValue);

  const style =
    resolveCertificateStyle(
      standard
    );

  if (
    !isValidDate(issueDate) ||
    !isValidDate(expiryDate)
  ) {
    return {
      dateOfCertification: null,
      firstSurveillanceAuditDue: null,
      secondSurveillanceAuditDue: null,
      registrationDate: null,
      surveillanceDate: null,
    };
  }

  /*
    Registration-style certificates.
  */
  if (
    style === 'registration'
  ) {
    const normalized =
      standard.toUpperCase();

    const surveillance =
      normalized.includes('38-1') ||
      normalized.includes('3834') ||
      normalized.includes('GMP')
        ? plusMonths(
            issueDate,
            11
          )
        : privacySurveillance(
            expiryDate
          );

    return {
      dateOfCertification: null,
      firstSurveillanceAuditDue: null,
      secondSurveillanceAuditDue: null,

      registrationDate:
        toInputDate(
          issueDate
        ),

      surveillanceDate:
        toInputDate(
          surveillance
        ),
    };
  }

  /*
    SURVEILLANCE CERTIFICATES

    Issue Date is the master date.

    Example:

    Issue Date:
    01-09-2026

    Initial Certification Date:
    01-09-2026

    1st Surveillance Due Date:
    01-09-2027

    2nd Surveillance Due Date:
    01-09-2028

    Certificate Expiry Date:
    01-09-2029
  */
  const validityYears =
    getCertificateValidityYears(
      issueDateValue,
      expiryDateValue
    );

  return {
    /*
      Same as Issue Date
    */
    dateOfCertification:
      toInputDate(
        issueDate
      ),

    /*
      Issue Date + 1 Year
    */
    firstSurveillanceAuditDue:
      toInputDate(
        plusYears(
          issueDate,
          1
        )
      ),

    /*
      Issue Date + 2 Years
      Only for 3-year certificates.
    */
    secondSurveillanceAuditDue:
      validityYears >= 3
        ? toInputDate(
            plusYears(
              issueDate,
              2
            )
          )
        : null,

    registrationDate: null,

    surveillanceDate: null,
  };
};

const getDateDisplay = (
  value?: string | null,
  dashFormat = false
) => {
  if (!value) {
    return '-';
  }

  const parsed =
    parseDateInput(value);

  if (
    !isValidDate(parsed)
  ) {
    return '-';
  }

  return dashFormat
    ? `(${formatDashDate(parsed)})`
    : formatOrdinalDate(
        parsed
      );
};

/*
  Format date in certificate table.
*/
const formatGridDate = (
  value?: string | null
) => {
  if (!value) {
    return '-';
  }

  /*
    API dates may contain
    full ISO datetime.
  */
  const dateOnly =
    value.includes('T')
      ? value.split('T')[0]
      : value;

  const parsed =
    parseDateInput(dateOnly);

  if (
    !isValidDate(parsed)
  ) {
    return '-';
  }

  return parsed.toLocaleDateString(
    'en-GB',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }
  );
};

const getDynamicFields = (
  request: CreateCertificateRequest
): DynamicFieldRow[] => {
  const style =
    resolveCertificateStyle(
      request.standard
    );

  /*
    Registration-style fields.
  */
  if (
    style === 'registration'
  ) {
    return [
      {
        label:
          'Certificate No',

        value:
          request.certificateNumber ||
          'Auto-generated on create',
      },
      {
        label:
          'Registration Date',

        value:
          getDateDisplay(
            request.registrationDate,
            true
          ),
      },
      {
        label:
          'Surveillance Date',

        value:
          getDateDisplay(
            request.surveillanceDate,
            true
          ),
      },
      {
        label:
          'Certificate Expiry',

        value:
          getDateDisplay(
            request.expiryDate,
            true
          ),
      },
    ];
  }

  /*
    Surveillance-style fields.
  */
  const validityYears =
    getCertificateValidityYears(
      request.issueDate,
      request.expiryDate
    );

  const fields:
    DynamicFieldRow[] = [
    {
      label:
        'Initial Certification Date',

      value:
        getDateDisplay(
          request.dateOfCertification
        ),
    },
    {
      label:
        'Certificate Issue Date',

      value:
        getDateDisplay(
          request.issueDate
        ),
    },
    {
      label:
        validityYears === 2
          ? 'Surveillance Due Date'
          : '1st Surveillance Due Date',

      value:
        getDateDisplay(
          request.firstSurveillanceAuditDue
        ),
    },
  ];

  if (
    validityYears >= 3
  ) {
    fields.push({
      label:
        '2nd Surveillance Due Date',

      value:
        getDateDisplay(
          request.secondSurveillanceAuditDue
        ),
    });
  }

  fields.push({
    label:
      'Certificate Expiry Date',

    value:
      getDateDisplay(
        request.expiryDate
      ),
  });

  return fields;
};

const getEmptyForm =
  (): CreateCertificateRequest => {
    const issueDate =
      getToday();

    const expiryDate =
      toInputDate(
        plusYears(
          parseDateInput(
            issueDate
          ),
          3
        )
      );

    const standard =
      'ISO 9001:2015';

    return {
      type: 'ISO',
      companyName: '',
      registeredAddress: '',
      branchAddress: '',
      standard,
      scope: '',
      certificateNumber: '',

      issueDate,

      expiryDate,

      ...getAutoDynamicDateFields(
        standard,
        issueDate,
        expiryDate
      ),
    };
  };

const CertificateCreatePage:
  React.FC = () => {
    const {
      loading,
      error,
      createCertificate,
      getCertificates,
      downloadCertificate,
      deleteCertificate,
    } = useCertificateApi();

    const { isAuthenticated } = useAuth();
    const isAdmin = isAuthenticated;

    const [
      successMessage,
      setSuccessMessage,
    ] = useState<string | null>(
      null
    );

    const [
      createdCertificateId,
      setCreatedCertificateId,
    ] = useState<string | null>(
      null
    );

    const [
      createdCertificateNumber,
      setCreatedCertificateNumber,
    ] = useState<string | null>(
      null
    );

    const [
      formData,
      setFormData,
    ] = useState<
      CreateCertificateRequest
    >(getEmptyForm);

    const standardOptions =
      STANDARD_OPTIONS_BY_TYPE[
        formData.type
      ];

    const [
      certificates,
      setCertificates,
    ] = useState<
      CertificateResponse[]
    >([]);

    const [
      downloadingCertificate,
      setDownloadingCertificate,
    ] = useState<{ id: string; format: string } | null>(
      null
    );

    const [deletingCertificateId, setDeletingCertificateId] = useState<string | null>(null);

    const [
      certificateSearch,
      setCertificateSearch,
    ] = useState('');

    const [
      statusFilter,
      setStatusFilter,
    ] = useState('all');

    const [
      standardFilter,
      setStandardFilter,
    ] = useState('all');

    const dynamicFields =
      getDynamicFields(
        formData
      );

    const loadCertificates =
      useCallback(
        async () => {
          const loadedCertificates =
            await getCertificates();

          setCertificates(
            loadedCertificates
          );
        },
        [getCertificates]
      );

    useEffect(() => {
      loadCertificates();
    }, [loadCertificates]);

    const handleInputChange = (
      e: React.ChangeEvent<
        | HTMLInputElement
        | HTMLTextAreaElement
        | HTMLSelectElement
      >
    ) => {
      const {
        name,
        value,
      } = e.target;

      setFormData(
        (prev) => {
          let updated:
            CreateCertificateRequest = {
              ...prev,
              [name]: value,
            };

          if (
            name === 'type'
          ) {
            const type =
              value as CertificateType;

            const standard =
              STANDARD_OPTIONS_BY_TYPE[
                type
              ][0].value;

            updated = {
              ...updated,
              type,
              standard,
            };

            return {
              ...updated,

              ...getAutoDynamicDateFields(
                updated.standard,
                updated.issueDate,
                updated.expiryDate
              ),
            };
          }

          /*
            ISSUE DATE CHANGE

            Issue Date becomes
            the master date.

            Expiry =
            Issue Date + 3 Years.

            All surveillance dates
            are recalculated.
          */
          if (
            name === 'issueDate'
          ) {
            const issueDate =
              parseDateInput(
                value
              );

            if (
              !isValidDate(
                issueDate
              )
            ) {
              return updated;
            }

            const expiryDate =
              toInputDate(
                plusYears(
                  issueDate,
                  3
                )
              );

            updated = {
              ...updated,
              issueDate: value,
              expiryDate,
            };

            return {
              ...updated,

              ...getAutoDynamicDateFields(
                updated.standard,
                updated.issueDate,
                updated.expiryDate
              ),
            };
          }

          /*
            STANDARD CHANGE
          */
          if (
            name === 'standard'
          ) {
            return {
              ...updated,

              ...getAutoDynamicDateFields(
                updated.standard,
                updated.issueDate,
                updated.expiryDate
              ),
            };
          }

          /*
            EXPIRY DATE CHANGE

            If manually changed,
            dynamic fields are
            recalculated.
          */
          if (
            name === 'expiryDate'
          ) {
            return {
              ...updated,

              ...getAutoDynamicDateFields(
                updated.standard,
                updated.issueDate,
                updated.expiryDate
              ),
            };
          }

          return updated;
        }
      );
    };

    /*
      Convert yyyy-MM-dd
      to ISO without changing
      the calendar date.
    */
    const toIsoOrNull = (
      value?: string | null
    ) => {
      if (!value) {
        return null;
      }

      const date =
        parseDateInput(
          value
        );

      if (
        !isValidDate(date)
      ) {
        return null;
      }

      /*
        Noon prevents common
        timezone date shifting.
      */
      return date.toISOString();
    };

    const handleSubmit = async (
      e: React.FormEvent
    ) => {
      e.preventDefault();

      setSuccessMessage(null);

      setCreatedCertificateId(
        null
      );

      setCreatedCertificateNumber(
        null
      );

      if (
        !formData.companyName.trim()
      ) {
        alert(
          'Company name is required'
        );

        return;
      }

      const certificate =
        await createCertificate({
          ...formData,

          issueDate:
            toIsoOrNull(
              formData.issueDate
            ) || '',

          expiryDate:
            toIsoOrNull(
              formData.expiryDate
            ) || '',

          dateOfCertification:
            toIsoOrNull(
              formData.dateOfCertification
            ),

          firstSurveillanceAuditDue:
            toIsoOrNull(
              formData.firstSurveillanceAuditDue
            ),

          secondSurveillanceAuditDue:
            toIsoOrNull(
              formData.secondSurveillanceAuditDue
            ),

          registrationDate:
            toIsoOrNull(
              formData.registrationDate
            ),

          surveillanceDate:
            toIsoOrNull(
              formData.surveillanceDate
            ),
        });

      if (
        certificate
      ) {
        setSuccessMessage(
          `Certificate created successfully: ${certificate.certificateNumber}`
        );

        setCreatedCertificateId(
          certificate.id
        );

        setCreatedCertificateNumber(
          certificate.certificateNumber
        );

        setCertificates(
          (prev) => [
            certificate,

            ...prev.filter(
              (item) =>
                item.id !==
                certificate.id
            ),
          ]
        );

        setFormData(
          getEmptyForm()
        );
      }
    };

    const handleDownload = async (
      certificateId = createdCertificateId,
      format: string = 'pdf'
    ) => {
      if (
        !certificateId
      ) {
        return;
      }

      setDownloadingCertificate({ id: certificateId, format });

      const downloaded = await downloadCertificate(certificateId, format);

      setDownloadingCertificate(null);

      if (downloaded) {
        setSuccessMessage('Certificate download started.');
      }
    };

    const resetCreatedCertificate =
      () => {
        setSuccessMessage(
          null
        );

        setCreatedCertificateId(
          null
        );

        setCreatedCertificateNumber(
          null
        );
      };

    const handleDelete = async (id: string) => {
      if (!window.confirm('Delete this certificate? This action cannot be undone.')) return;

      try {
        setDeletingCertificateId(id);
        const deleted = await deleteCertificate(id);

        if (deleted) {
          setCertificates((prev: CertificateResponse[]) => prev.filter((c) => c.id !== id));
          setSuccessMessage('Certificate deleted');
        }
      } finally {
        setDeletingCertificateId(null);
      }
    };

    const certificateStyle =
      resolveCertificateStyle(
        formData.standard
      );

    const certificateValidityYears =
      getCertificateValidityYears(
        formData.issueDate,
        formData.expiryDate
      );

    const isTwoYearCertificate =
      certificateStyle ===
        'surveillance' &&
      certificateValidityYears === 2;

    const isThreeYearCertificate =
      certificateStyle ===
        'surveillance' &&
      certificateValidityYears >= 3;

    const standardFilterOptions =
      useMemo(
        () =>
          Array.from(
            new Set(
              certificates
                .map(
                  (certificate) =>
                    certificate.standard
                )
                .filter(Boolean)
            )
          ).sort(),
        [certificates]
      );

    const filteredCertificates =
      useMemo(() => {
        const normalizedSearch =
          certificateSearch
            .trim()
            .toLowerCase();

        return certificates.filter(
          (certificate) => {
            const matchesSearch =
              !normalizedSearch ||
              [
                certificate.certificateNumber,
                certificate.companyName,
                certificate.type,
                certificate.standard,
                certificate.status,
                formatGridDate(
                  certificate.issueDate
                ),
                formatGridDate(
                  certificate.expiryDate
                ),
              ]
                .filter(Boolean)
                .some((value) =>
                  value
                    .toLowerCase()
                    .includes(
                      normalizedSearch
                    )
                );

            const matchesStatus =
              statusFilter ===
                'all' ||
              certificate.status
                .toLowerCase() ===
                statusFilter;

            const matchesStandard =
              standardFilter ===
                'all' ||
              certificate.standard ===
                standardFilter;

            return (
              matchesSearch &&
              matchesStatus &&
              matchesStandard
            );
          }
        );
      }, [
        certificateSearch,
        certificates,
        standardFilter,
        statusFilter,
      ]);

    return (
      <div className="create-certificate-container">

        <section className="create-hero">
          <div>
            <span className="eyebrow">
              Admin Workspace
            </span>

            <h1 className="page-title">
              Create Certificate
            </h1>

            <p className="page-description">
              Issue a verified PDF certificate
              with an embedded QR code.
            </p>
          </div>



          <div
            className="hero-metrics"
            aria-label="Certificate workflow"
          >
            <div>
              <span>01</span>
              <strong>Create</strong>
            </div>

            <div>
              <span>02</span>
              <strong>Download</strong>
            </div>

            <div>
              <span>03</span>
              <strong>Verify</strong>
            </div>
          </div>
        </section>

        <div className="create-certificate-card">

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="alert alert-success">
              {successMessage}
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="certificate-form"
          >

            <fieldset className="form-fieldset">
              <legend>
                Company Information
              </legend>

              <div className="form-group">
                <label htmlFor="companyName">
                  Company Name *
                </label>

                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={
                    formData.companyName
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Enter company name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="registeredAddress">
                  Registered Address
                </label>

                <input
                  type="text"
                  id="registeredAddress"
                  name="registeredAddress"
                  value={
                    formData.registeredAddress
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Enter registered address"
                />
              </div>

              <div className="form-group">
                <label htmlFor="branchAddress">
                  Branch Address
                </label>

                <input
                  type="text"
                  id="branchAddress"
                  name="branchAddress"
                  value={
                    formData.branchAddress
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Enter branch address"
                />
              </div>
            </fieldset>

            <fieldset className="form-fieldset">
              <legend>
                Certification Details
              </legend>

              <div className="form-group">
                <label htmlFor="type">
                  Certificate Type
                </label>

                <select
                  id="type"
                  name="type"
                  value={
                    formData.type
                  }
                  onChange={
                    handleInputChange
                  }
                >
                  {CERTIFICATE_TYPE_OPTIONS.map(
                    (option) => (
                      <option
                        key={
                          option.value
                        }
                        value={
                          option.value
                        }
                      >
                        {option.label}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="standard">
                  Certification Standard
                </label>

                <select
                  id="standard"
                  name="standard"
                  value={
                    formData.standard
                  }
                  onChange={
                    handleInputChange
                  }
                >
                  {standardOptions.map(
                    (option) => (
                      <option
                        key={
                          option.value
                        }
                        value={
                          option.value
                        }
                      >
                        {option.label}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="scope">
                  Scope of Certification *
                </label>

                <textarea
                  id="scope"
                  name="scope"
                  value={
                    formData.scope
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Describe the scope of certification activities"
                  rows={4}
                />
              </div>
            </fieldset>

            <fieldset className="form-fieldset">
              <legend>
                Certificate Details
              </legend>

              <div className="form-group">
                <label>
                  Certificate Number
                </label>

                <input
                  type="text"
                  value="Auto-generated"
                  readOnly
                  disabled
                />
              </div>

              <div className="form-row">

                <div className="form-group">
                  <label htmlFor="issueDate">
                    Issue Date *
                  </label>

                  <input
                    type="date"
                    id="issueDate"
                    name="issueDate"
                    value={
                      formData.issueDate
                    }
                    onChange={
                      handleInputChange
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="expiryDate">
                    Expiry Date *
                  </label>

                  <input
                    type="date"
                    id="expiryDate"
                    name="expiryDate"
                    value={
                      formData.expiryDate
                    }
                    onChange={
                      handleInputChange
                    }
                    required
                  />
                </div>

              </div>

              {certificateStyle ===
                'surveillance' && (
                <div className="form-row">

                  <div className="form-group">
                    <label htmlFor="dateOfCertification">
                      Initial Certification Date
                    </label>

                    <input
                      type="date"
                      id="dateOfCertification"
                      name="dateOfCertification"
                      value={
                        formData.dateOfCertification ||
                        ''
                      }
                      readOnly
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="firstSurveillanceAuditDue">
                      {isTwoYearCertificate
                        ? 'Surveillance Due Date'
                        : '1st Surveillance Due Date'}
                    </label>

                    <input
                      type="date"
                      id="firstSurveillanceAuditDue"
                      name="firstSurveillanceAuditDue"
                      value={
                        formData.firstSurveillanceAuditDue ||
                        ''
                      }
                      readOnly
                    />
                  </div>

                </div>
              )}

              {isThreeYearCertificate && (
                <div className="form-row">

                  <div className="form-group">
                    <label htmlFor="secondSurveillanceAuditDue">
                      2nd Surveillance Due Date
                    </label>

                    <input
                      type="date"
                      id="secondSurveillanceAuditDue"
                      name="secondSurveillanceAuditDue"
                      value={
                        formData.secondSurveillanceAuditDue ||
                        ''
                      }
                      readOnly
                    />
                  </div>

                </div>
              )}

              {certificateStyle ===
                'registration' && (
                <div className="form-row">

                  <div className="form-group">
                    <label htmlFor="registrationDate">
                      Registration Date
                    </label>

                    <input
                      type="date"
                      id="registrationDate"
                      name="registrationDate"
                      value={
                        formData.registrationDate ||
                        ''
                      }
                      onChange={
                        handleInputChange
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="surveillanceDate">
                      Surveillance Date
                    </label>

                    <input
                      type="date"
                      id="surveillanceDate"
                      name="surveillanceDate"
                      value={
                        formData.surveillanceDate ||
                        ''
                      }
                      onChange={
                        handleInputChange
                      }
                    />
                  </div>

                </div>
              )}

              <div
                className="dynamic-fields-preview"
                aria-live="polite"
              >
                <h4>
                  Certificate Dynamic Fields Preview
                </h4>

                <p>
                  These values are automatically
                  generated based on the selected
                  certificate standard and dates.
                </p>

                <div className="dynamic-fields-grid">

                  {dynamicFields.map(
                    (field) => (
                      <div
                        key={
                          field.label
                        }
                        className="dynamic-field-row"
                      >
                        <span>
                          {field.label}
                        </span>

                        <strong>
                          {field.value}
                        </strong>
                      </div>
                    )
                  )}

                </div>
              </div>

            </fieldset>

            <fieldset className="form-fieldset">
              <legend>
                Authorization
              </legend>

              <div className="digital-signature-note">
                <strong>
                  Built-in digital signature
                </strong>

                <span>
                  Every generated PDF uses the
                  system digital signature
                  automatically.
                </span>
              </div>
            </fieldset>

            <button
              type="submit"
              className="btn btn-primary btn-large"
              disabled={loading}
            >
              {loading
                ? 'Creating Certificate...'
                : 'Create Certificate'}
            </button>

          </form>

          {createdCertificateId && (
            <div className="certificate-created-info">

              <div
                className="success-icon"
                aria-hidden="true"
              >
                OK
              </div>

              <div className="success-copy">
                <h3>
                  Certificate ready
                </h3>

                <p>
                  The PDF has been generated and
                  is ready for admin download.
                </p>
              </div>

              <div className="certificate-id-panel">

                <span>
                  Certificate Number
                </span>

                <strong>
                  {
                    createdCertificateNumber
                  }
                </strong>

                <span>
                  Certificate ID
                </span>

                <code>
                  {createdCertificateId}
                </code>

              </div>

              <div className="certificate-info-actions">

                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-primary download-button"
                    type="button"
                    onClick={() => handleDownload(undefined, 'pdf')}
                    disabled={downloadingCertificate?.id === createdCertificateId && downloadingCertificate?.format === 'pdf'}
                  >
                    {downloadingCertificate?.id === createdCertificateId && downloadingCertificate?.format === 'pdf'
                      ? 'Preparing...'
                      : 'Download PDF'}
                  </button>

                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => handleDownload(undefined, 'png')}
                    disabled={downloadingCertificate?.id === createdCertificateId && downloadingCertificate?.format === 'png'}
                  >
                    {downloadingCertificate?.id === createdCertificateId && downloadingCertificate?.format === 'png'
                      ? 'Preparing...'
                      : 'Download Image'}
                  </button>
                </div>

                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={
                    resetCreatedCertificate
                  }
                >
                  Create Another Certificate
                </button>

              </div>

            </div>
          )}

        </div>

        <section className="created-certificates-section">

          <div className="section-heading">

            <div>

              <span className="eyebrow">
                Created Certificates
              </span>

              <h2>
                All Certificates
              </h2>

              <p className="table-result-summary">
                Showing {
                  filteredCertificates.length
                }{' '}
                of {
                  certificates.length
                } certificates
              </p>

            </div>

            <button
              className="btn btn-secondary"
              type="button"
              onClick={
                loadCertificates
              }
              disabled={loading}
            >
              Refresh
            </button>

          </div>

          <div
            className="certificate-table-toolbar"
            aria-label="Certificate table filters"
          >

            <label className="table-search-field">

              <span>
                Search
              </span>

              <input
                type="search"
                value={
                  certificateSearch
                }
                onChange={(event) =>
                  setCertificateSearch(
                    event.target.value
                  )
                }
                placeholder="Certificate no, company, standard..."
              />

            </label>

            <label className="table-filter-field">

              <span>
                Status
              </span>

              <select
                value={
                  statusFilter
                }
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value
                  )
                }
              >

                <option value="all">
                  All statuses
                </option>

                <option value="valid">
                  Valid
                </option>

                <option value="expired">
                  Expired
                </option>

                <option value="suspended">
                  Suspended
                </option>

              </select>

            </label>

            <label className="table-filter-field">

              <span>
                Standard
              </span>

              <select
                value={
                  standardFilter
                }
                onChange={(event) =>
                  setStandardFilter(
                    event.target.value
                  )
                }
              >

                <option value="all">
                  All standards
                </option>

                {standardFilterOptions.map(
                  (standard) => (
                    <option
                      key={
                        standard
                      }
                      value={
                        standard
                      }
                    >
                      {standard}
                    </option>
                  )
                )}

              </select>

            </label>

            <button
              className="btn btn-secondary table-clear-button"
              type="button"
              onClick={() => {
                setCertificateSearch('');
                setStatusFilter('all');
                setStandardFilter('all');
              }}
            >
              Clear
            </button>

          </div>

          <div className="created-certificates-grid">

            <table>

              <thead>
                <tr>

                  <th>
                    Action
                  </th>

                  <th>
                    Certificate No
                  </th>

                  <th>
                    Company
                  </th>

                  <th>
                    Type
                  </th>

                  <th>
                    Standard
                  </th>

                  <th>
                    Issue Date
                  </th>

                  <th>
                    Expiry Date
                  </th>

                  <th>
                    Status
                  </th>

                  <th>
                    Created
                  </th>

                </tr>
              </thead>

              <tbody>

                {certificates.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="empty-grid-cell"
                    >
                      No certificates created yet.
                    </td>
                  </tr>
                ) : filteredCertificates.length ===
                  0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="empty-grid-cell"
                    >
                      No certificates match your
                      search or filters.
                    </td>
                  </tr>
                ) : (
                  filteredCertificates.map(
                    (certificate) => (
                      <tr
                        key={
                          certificate.id
                        }
                      >

                        <td>

                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="btn btn-secondary grid-download-button"
                              type="button"
                              onClick={() =>
                                handleDownload(certificate.id, 'pdf')
                              }
                              disabled={
                                downloadingCertificate?.id === certificate.id && downloadingCertificate?.format === 'pdf'
                              }
                            >
                              {downloadingCertificate?.id === certificate.id && downloadingCertificate?.format === 'pdf'
                                ? 'Preparing...'
                                : 'PDF'}
                            </button>

                            <button
                              className="btn btn-secondary grid-download-button"
                              type="button"
                              onClick={() =>
                                handleDownload(certificate.id, 'png')
                              }
                              disabled={
                                downloadingCertificate?.id === certificate.id && downloadingCertificate?.format === 'png'
                              }
                            >
                              {downloadingCertificate?.id === certificate.id && downloadingCertificate?.format === 'png'
                                ? 'Preparing...'
                                : 'Image'}
                            </button>
                            {isAdmin && (
                              <button
                                className="btn btn-danger"
                                type="button"
                                onClick={() => handleDelete(certificate.id)}
                                disabled={Boolean(deletingCertificateId) || loading}
                                style={{ marginLeft: 6 }}
                              >
                                {deletingCertificateId === certificate.id ? 'Deleting...' : 'Delete'}
                              </button>
                            )}
                          </div>

                        </td>

                        <td className="certificate-number-cell">
                          {
                            certificate.certificateNumber
                          }
                        </td>

                        <td className="company-cell">
                          {
                            certificate.companyName
                          }
                        </td>

                        <td>
                          {
                            certificate.type
                          }
                        </td>

                        <td className="standard-cell">
                          {
                            certificate.standard
                          }
                        </td>

                        <td>
                          {formatGridDate(
                            certificate.issueDate
                          )}
                        </td>

                        <td>
                          {formatGridDate(
                            certificate.expiryDate
                          )}
                        </td>

                        <td>

                          <span
                            className={`status-pill status-${certificate.status.toLowerCase()}`}
                          >
                            {
                              certificate.status
                            }
                          </span>

                        </td>

                        <td>
                          {formatGridDate(
                            certificate.createdDate
                          )}
                        </td>

                      </tr>
                    )
                  )
                )}

              </tbody>

            </table>

          </div>

        </section>

      </div>
    );
  };

export default CertificateCreatePage;
